import { StyleProp, View, ViewStyle } from 'react-native';
import Stack from '../Stack';
import Checkbox from '../Inputs/Checkbox';
import { colors } from '../../../utils/theme';
import Text from '../Text';
import { getSpacing } from '../../../utils/spacing';
import React from 'react';
import IsDisabledProvider, { useIsDisabledByParent } from '../IsDisabledProvider';

export type CheckboxConfigValue = boolean | 'indeterminate';

export type CheckboxConfig = {
    onToggle: (isChecked: boolean) => void;
    value: CheckboxConfigValue;
    testID: string;
    isDisabled?: boolean;
};

export type ConfigurationTableProps = {
    title: string;
    checkboxTitles: string[];
    children: React.ReactNode;
    checkboxWidth: number;
    isDisabled?: boolean;
};

export type RowProps = {
    title: string;
    checkboxes: CheckboxConfig[];
    isGroupRow?: boolean;
    isLastInGroupRow?: boolean;
};

export const textPaddingStyle: StyleProp<ViewStyle> = {
    padding: getSpacing(3.5),
    paddingLeft: getSpacing(5),
};

const CheckboxWidthContext = React.createContext<number>(100);

const CheckboxGroup: React.FC<{ checkboxes: CheckboxConfig[] }> = ({ checkboxes }) => {
    const checkboxWidth = React.useContext(CheckboxWidthContext);
    return (
        <Stack horizontal justify="space-around">
            {checkboxes.map((checkbox, index) => (
                <Stack horizontal justify="center" align="center" style={{ width: checkboxWidth }} key={index}>
                    <View>
                        <Checkbox
                            onChange={checkbox.onToggle}
                            value={checkbox.value === 'indeterminate' ? false : checkbox.value}
                            testID={checkbox.testID}
                            indeterminate={checkbox.value === 'indeterminate'}
                            isDisabled={checkbox.isDisabled}
                        />
                    </View>
                </Stack>
            ))}
        </Stack>
    );
};

export const ConfigurableTableRow: React.FC<RowProps> = ({ title, checkboxes, isGroupRow, isLastInGroupRow }) => {
    return (
        <Stack
            horizontal
            align="center"
            justify="space-between"
            fullWidth
            style={
                isGroupRow
                    ? { backgroundColor: colors['Background'] }
                    : {
                          backgroundColor: isGroupRow ? colors['Background'] : undefined,
                          borderBottomColor: isLastInGroupRow ? 'transparent' : colors['Dividers'],
                          borderBottomWidth: 1,
                      }
            }
        >
            <Text variant={isGroupRow ? 'permissions-secondary-header' : 'h6'} style={textPaddingStyle}>
                {title}
            </Text>
            <CheckboxGroup checkboxes={checkboxes} />
        </Stack>
    );
};

export const ConfigurableTable: React.FC<ConfigurationTableProps> = ({
    title,
    checkboxTitles,
    children,
    checkboxWidth,
    isDisabled,
}) => {
    const isDisabledByParent = useIsDisabledByParent(isDisabled);
    return (
        <CheckboxWidthContext.Provider value={checkboxWidth}>
            <IsDisabledProvider isDisabled={isDisabledByParent}>
                <Stack vertical fullWidth space={getSpacing(2.5)} style={{ backgroundColor: 'white' }}>
                    <Stack horizontal align="center" justify="space-between" fullWidth>
                        <Text variant="permissions-primary-header" style={textPaddingStyle}>
                            {title}
                        </Text>
                        <Stack horizontal justify="flex-end">
                            {checkboxTitles.map((title, index) => (
                                <Text
                                    variant="permissions-secondary-header"
                                    alignText="center"
                                    style={{ width: checkboxWidth }}
                                    key={index}
                                >
                                    {title}
                                </Text>
                            ))}
                        </Stack>
                    </Stack>
                    <Stack vertical fullWidth>
                        {children}
                    </Stack>
                </Stack>
            </IsDisabledProvider>
        </CheckboxWidthContext.Provider>
    );
};
